import { Cancel, Check, Edit } from "@mui/icons-material";
import { Menu, MenuItem, TextField } from "@mui/material";
import { CellContext } from "@tanstack/react-table";
import LoadingSpinner from "components/LoadingSpinner";
import { TagView } from "generated/graphql";
import { HTMLProps, useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectLensEditorTagColors } from "redux/slices/lensEditor";

export function ColorSwatch(props: HTMLProps<HTMLSpanElement>) {
  return <span {...props} className="rounded-full h-4 w-4 inline-block" />;
}

interface ColorPickerProps extends HTMLProps<HTMLSpanElement> {
  onSelectColor: (color: string, colorValue: string) => void;
  editing: boolean;
}
function ColorPicker({ onSelectColor, editing, ...props }: ColorPickerProps) {
  const tagColors = useAppSelector(selectLensEditorTagColors);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = editing && !!anchorEl;

  const handleClose = () => setAnchorEl(null);
  return (
    <>
      <ColorSwatch
        title={editing ? "" : "Edit to change the color"}
        {...props}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} MenuListProps={{ className: "grid grid-cols-2" }}>
        {tagColors.map((color) => (
          <MenuItem
            key={color.value}
            title={color.displayName}
            onClick={() => {
              onSelectColor(color.color, color.value);
              handleClose();
            }}>
            <ColorSwatch style={{ backgroundColor: color.value }} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

type ExtraProps = {
  onSave: (t: TagView) => Promise<void>;
};

export function TagName({ row: { original }, onSave }: CellContext<TagView, string> & ExtraProps) {
  const [tag, setTag] = useState(original);
  const [checkpoint, setCheckpoint] = useState(tag);
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState(false);
  const handleSave = async () => {
    setPending(true);
    try {
      await onSave(tag);
      setCheckpoint(tag);
    } finally {
      setEditing(false);
      setPending(false);
    }
  };
  const onEdit = async () => {
    setEditing(true);
    setCheckpoint(tag);
  };
  const onCancel = async () => {
    setEditing(false);
    setTag(checkpoint);
  };

  // Side effect: Want this to run when color is changed by color picker, so we don't have to double confirm
  // Can use a class component or write a useStateCallback hook to achieve the same.
  useEffect(() => {
    if (tag.color !== checkpoint.color) handleSave();
  }, [tag.color]);

  return (
    <span className="w-full justify-between inline-flex items-center gap-1" onClick={() => !editing && onEdit()}>
      {editing ? (
        <TextField
          autoFocus
          fullWidth
          variant="standard"
          className="bg-white p-0 focus:ring-peri-300"
          value={tag.displayName}
          onChange={(e) => setTag((t) => ({ ...t, displayName: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSave();
            if (e.key === "Esc") onCancel();
          }}
        />
      ) : (
        <span className="py-1">{tag.displayName}</span>
      )}
      <div className="flex items-center gap-1">
        <ColorPicker
          editing={editing}
          onSelectColor={(color, colorValue) => {
            setTag((t) => ({ ...t, color, colorValue }) as TagView);
          }}
          style={{ backgroundColor: tag.colorValue }}
        />
        {pending && <LoadingSpinner size={16} />}
        {!pending &&
          (!editing ? (
            <Edit tabIndex={0} className="h-4 w-4 opacity-50 cursor-pointer" onClick={onEdit} />
          ) : (
            <>
              <Cancel tabIndex={0} className="h-4 w-4 opacity-50 cursor-pointer" onClick={onCancel} />
              <Check tabIndex={0} className="h-4 w-4 opacity-50 cursor-pointer" onClick={handleSave} />
            </>
          ))}
      </div>
    </span>
  );
}
